import type { Copy } from "../types";

export const hu: Copy = {
    seo: {
      title: "Járműkor kalkulátor | év és hónap alapján",
      description:
        "Ellenőrizd egy jármű pontos korát a gyártási év és az opcionális hónap alapján. Évek, hónapok, napok, összes nap és mérföldkövek egy tiszta eszközben.",
      keywords: [
        "járműkor kalkulátor",
        "autó kora",
        "jármű kora év alapján",
        "autó gyártási éve",
        "jármű gyártási hónap",
        "hány éves az autóm",
        "járműkor ellenőrző",
        "RC kor kalkulátor",
        "jármű dátum kalkulátor",
        "autó kor kalkulátor hónappal",
        "mParivahan alternatíva",
        "Parivahan alternatíva",
        "RTO járműkor kalkulátor",
        "autó regisztrációs kor ellenőrző",
        "jármű regisztrációs kor",
      ],
      faq: [
        {
          question: "Mit csinál ez a kalkulátor?",
          answer:
            "A kiválasztott gyártási évből és az opcionális hónapból pontosan kiszámítja a jármű korát, majd megmutatja az éveket, hónapokat, napokat és a mérföldköveket.",
        },
        {
          question: "Miért van opcionális hónap?",
          answer:
            "A hónap megadása pontosabbá teszi a kort. Ha kihagyod a hónapot, az alkalmazás a kiválasztott év januárját használja.",
        },
        {
          question: "Választhatok jövőbeli évet vagy hónapot?",
          answer:
            "Nem. A jövőbeli évek le vannak tiltva, és a jövőbeli hónapok inaktívak, ha a kiválasztott év az aktuális év.",
        },
      ],
    },
    headerTagline: "Jármű segédlet",
    headerTitle: "Járműkor kalkulátor",
    reset: "Alaphelyzet",
    switchToLight: "Világos mód",
    switchToDark: "Sötét mód",
    languageLabel: "Nyelv",
    languageEnglish: "Angol",
    languageHindi: "Hindi",
    heroEyebrow: "Autó kora, tisztán",
    heroTitle: "Tiszta dátumeszköz a jármű korához.",
    heroDescription:
      "Válaszd ki a gyártási évet. Hónapot csak akkor adj meg, ha pontosabb eredményt szeretnél.",
    yearSelectLabel: "Gyártási év",
    monthSelectLabel: "Gyártási hónap",
    includeMonthLabel: "Pontosabb eredményhez add meg a konkrét hónapot",
    includeMonthNote: "Ha nem adsz meg hónapot, a számítás januárral indul.",
    yearOnlyBadge: "Csak év",
    yearMonthBadge: "Év + hónap",
    yearPlaceholder: "Válassz évet",
    monthPlaceholder: "Válassz hónapot",
    inputTitle: "Gyártási év",
    inputLabel: "Gyártási év",
    blockedDateNote: "A jövőbeli évek le vannak tiltva.",
    result: "Eredmény",
    vehicleAgeLead: "Az Ön járműve",
    asOfToday: "Mai állapot szerint",
    years: "Év",
    months: "Hónap",
    days: "Nap",
    totalDays: (count) => `${count.toLocaleString("en-IN")} összes nap`,
    ageLabels: { almostNew: "Majdnem új", modern: "Modern", mature: "Érettebb", classic: "Klasszikus" },
    estimatedValue: {
      high: "Magas továbbértékesítési potenciál",
      healthy: "Jó értéktartás",
      moderate: "Mérsékelt értékcsökkenés",
      collector: "Nőhet a gyűjtői érdeklődés",
    },
    maintenanceTitle: "Karbantartás",
    maintenance: {
      routine: "Rendszeres szerviz és OEM gondozás",
      watch: "Figyeld a fékeket, folyadékokat és gumikat",
      plan: "Tervezd be a kopó alkatrészeket és az ellenőrzéseket",
      specialized: "A speciális karbantartás egyre fontosabb",
    },
    registrationTitle: "Nyilvántartás",
    registration: {
      collector: "Egyes régiókban gyűjtői besorolás is alkalmazható",
      classic: "Egyes régiókban klasszikus autó besorolás is érvényesülhet",
      standard: "Általában a normál regisztráció érvényes",
      regular: "Általában a rendes regisztráció érvényes",
    },
    upcomingMilestoneTitle: "Következő mérföldkő",
    milestoneTimelineTitle: "Mérföldkő-ütemterv",
    milestoneRemaining: ({ label, days }) => `${label} ${days.toLocaleString("hu-HU")} nap múlva`,
    reached: "Elérve",
    upcoming: "Következő",
    allMilestonesReachedTitle: "Az összes felsorolt mérföldkő elérve",
    allMilestonesReachedBody: "Ez a jármű már túllépte az alkalmazásban jelenleg megjelenített mérföldkő-tartományt.",
    copySummary: "Összefoglaló másolása",
    shareResult: "Eredmény megosztása",
    resetMessage: "A kalkulátor alaphelyzetbe állítva",
    summaryCopied: "Összefoglaló a vágólapra másolva",
    clipboardFailed: "A vágólap elérése sikertelen",
    emptyTitle: "A teljes bontáshoz válaszd ki a gyártási évet",
    emptyDescription:
      "A pontos kor, az összes nap, a karbantartási megjegyzések, a klasszikus státusz és a mérföldkövek időzítése a gyártási év kiválasztása után jelenik meg.",
    shareTitle: "Járműkor kalkulátor",
    summary: ({ label, years, months, days, asOf }) =>
      `A járművem gyártási ideje ${label}, és ${asOf} szerint ${years} éves, ${months} hónapos és ${days} napos.`,
    milestones: {
      1: { label: "Első év", note: "Hasznos a garancia és a szerviztervezés szempontjából." },
      3: { label: "Három év", note: "Gyakori pont az eladás és az értékváltozás szempontjából." },
      5: { label: "Öt év", note: "Jó időpont a komolyabb karbantartási költségvetésre." },
      10: { label: "Tíz év", note: "A régebbi alkatrészek több figyelmet igényelnek." },
      15: { label: "Tizenöt év", note: "Sok régióban itt kezdődik a klasszikus megítélés." },
      25: { label: "Huszonöt év", note: "Gyűjtők és importpiacok számára is vonzó lehet." },
    },
  };
